import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { 
  Edit, Clock, Calendar, BookOpen, FileText, FilePlus2,
  AlertTriangle, MapPin
} from "lucide-react";
import { toast } from "react-toastify";
import EditQualificationDialog from "./EditQualificationDialog";
import TrainingEditModal from "./TrainingEditModal";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;






// Main UserDayView component
const UserDayView = ({ user, date, userWork, userEvents, userExpirations, trainingOptions, userQualifications, companyId, onClose, fetchData, isUserColumnClick }) => {
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleTraining, setShowScheduleTraining] = useState(false);
  const [editingQualification, setEditingQualification] = useState(undefined); // Zmieniono inicjalny stan na undefined
  const [trainingEditData, setTrainingEditData] = useState(null);
  const [showQualificationsTab, setShowQualificationsTab] = useState(isUserColumnClick || false);
  const [userQualificationsList, setUserQualificationsList] = useState([]);
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [sortByExpiry, setSortByExpiry] = useState(true);


  // Handler for opening new training modal
  const handleScheduleNewTraining = () => {
    // Use the date from UserDayView context (the day user clicked on)
    const trainingDate = new Date(date);
    trainingDate.setHours(9, 0, 0, 0); // Set default time to 9:00 AM

    setTrainingEditData({
      isNew: true,
      users: [user.id], // Preselect current user
      start_date: trainingDate.toISOString(),
      end_date: trainingDate.toISOString()
    });
    setShowScheduleTraining(true);
  };

  useEffect(() => {
    const filteredWork = userWork.filter((item) => {
      const itemDate = new Date(item.planned_date_start);
      return itemDate.toDateString() === date.toDateString() && item.userId === user.username;
    });

    const filteredEvents = userEvents.filter((item) => {
      const itemDate = new Date(item.start_date || item.planned_date_start);
      return itemDate.toDateString() === date.toDateString() && item.userId === user.id;
    });

    const filteredExpirations = userExpirations.filter((item) => {
      const itemDate = new Date(item.start_date || item.expiryDate);
      return itemDate.toDateString() === date.toDateString() && item.userId === user.username;
    });

    setOrderDetails([...filteredEvents, ...filteredExpirations]);
    
    const userQuals = userQualifications.filter((qual) => qual.user === user.id);
    setUserQualificationsList(userQuals);
    
    if (isUserColumnClick || user.openQualification) {
      setShowQualificationsTab(true);
      if (user.qualificationId) {
        const qualification = userQualifications.find(q => q.id === user.qualificationId);
        if (qualification) {
          setEditingQualification(qualification);
        }
      }
    }
    
    setLoading(false);
  }, [user, date, userWork, userEvents, userExpirations, userQualifications, isUserColumnClick]);

  const getFilteredQualifications = () => {
    let filtered = [...userQualificationsList];
    
    if (showExpiringOnly) {
      filtered = filtered.filter(qual => {
        if (!qual.expiry_date) return false;
        const daysInfo = getDaysRemaining(qual.expiry_date);
        return daysInfo.expired || daysInfo.days <= 60;
      });
    }
    
    if (sortByExpiry) {
      filtered.sort((a, b) => {
        if (!a.expiry_date && !b.expiry_date) return 0;
        if (!a.expiry_date) return 1;
        if (!b.expiry_date) return -1;
        return new Date(a.expiry_date) - new Date(b.expiry_date);
      });
    }
    
    return filtered;
  };

  const handleEditTraining = async (item) => {
    console.log('UserDayView handleEditTraining called with item:', item);
    
    // Extract training ID from the item
    let trainingId = null;
    if (item.id && typeof item.id === 'string' && item.id.startsWith('training-')) {
      trainingId = parseInt(item.id.replace('training-', ''));
    } else if (item.originalTrainingId) {
      trainingId = item.originalTrainingId;
    } else if (typeof item.id === 'number') {
      trainingId = item.id;
    }
    
    if (trainingId) {
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(`${API_BASE_URL}/trainings/${trainingId}/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const trainingData = await response.json();
          console.log('Fetched training data:', trainingData);
          setTrainingEditData(trainingData);
          setShowScheduleTraining(true);
        } else {
          console.error('Failed to fetch training data:', response.status);
          toast.error('Failed to load training data');
        }
      } catch (error) {
        console.error('Error fetching training data:', error);
        toast.error('Error loading training data');
      }
    } else {
      console.error('Could not extract training ID from item:', item);
    }
  };


  const handleEditQualification = (qualification) => {
    setEditingQualification(qualification);
  };

  const handleAddQualification = () => {
    setEditingQualification(null); // Ustawiamy null dla trybu tworzenia
  };




  const getDaysRemaining = (expiryDateStr) => {
    if (!expiryDateStr) return null;
    
    const expiryDate = new Date(expiryDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    
    if (expiryDate.getTime() <= today.getTime()) {
      const daysAgo = Math.round((today - expiryDate) / (1000 * 60 * 60 * 24));
      return { expired: true, days: daysAgo };
    }
    
    const daysLeft = Math.round((expiryDate - today) / (1000 * 60 * 60 * 24));
    return { expired: false, days: daysLeft };
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={`${showScheduleTraining || orderDetails.length > 2 ? 'sm:max-w-[1100px]' : 'sm:max-w-[800px]'} max-h-[95vh] overflow-hidden flex flex-col`} autoFocus={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <div>
              {isUserColumnClick ? (
                <span className="font-bold text-blue-600">{user.first_name} {user.last_name}</span>
              ) : (
                <>
                  <span className="font-bold text-blue-600">{user.first_name} {user.last_name}</span>
                  <span className="text-gray-600"> - </span>
                  <span className="text-gray-600 font-medium">
                    {new Date(date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                  </span>
                </>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-gray-500 text-sm">Loading data...</p>
            </div>
          ) : (
            <div className="space-y-4 p-4 min-h-0 flex-1">
            {!isUserColumnClick && (
              <div className="flex border-b">
                <button
                  onClick={() => setShowQualificationsTab(false)}
                  className={`px-4 py-2 text-sm font-medium ${!showQualificationsTab ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  Daily Events
                </button>
                <button
                  onClick={() => setShowQualificationsTab(true)}
                  className={`px-4 py-2 text-sm font-medium ${showQualificationsTab ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  Qualifications
                </button>
              </div>
            )}
            
            {/* Daily Events Tab Content */}
            {(!isUserColumnClick && !showQualificationsTab) ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Events for this day ({orderDetails.length})
                  </h3>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={handleScheduleNewTraining}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Schedule New Training
                    </Button>
                  </div>
                </div>

                

                {orderDetails.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {orderDetails.map((item) => {
                      const isQualificationAlert = item.type === "qualificationalert";
                      const bgClass = isQualificationAlert
                        ? (item.daysRemaining < 0
                            ? "bg-red-100 border-red-300"
                            : item.daysRemaining <= 30
                              ? "bg-orange-100 border-orange-300"
                              : item.daysRemaining <= 60
                                ? "bg-yellow-100 border-yellow-300"
                                : "bg-green-100 border-green-300")
                        : item.type === "work"
                          ? "bg-gray-100 border-gray-300"
                          : item.type === "training"
                            ? "bg-blue-100 border-blue-300"
                            : "bg-green-100 border-green-300";
                      const borderColor = isQualificationAlert
                        ? (item.daysRemaining < 0
                            ? "bg-red-500"
                            : item.daysRemaining <= 30
                              ? "bg-orange-500"
                              : item.daysRemaining <= 60
                                ? "bg-yellow-500"
                                : "bg-green-500")
                        : item.type === "work"
                          ? "bg-gray-500"
                          : item.type === "training"
                            ? "bg-blue-500"
                            : "bg-green-500";

                      return (
                        <div
                          key={item.id}
                          className={`border rounded-lg p-3 shadow-sm relative overflow-hidden ${bgClass}`}
                        >
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${borderColor}`}></div>
                          <div className="pl-2">
                            <div className="font-medium text-sm flex items-center justify-between">
                              <div className="flex items-center">
                                {isQualificationAlert ? (
                                  item.daysRemaining <= 60 ? (
                                    <>
                                      <AlertTriangle
                                        className={`h-4 w-4 mr-2 ${
                                          item.daysRemaining < 0 ? "text-red-600" :
                                          item.daysRemaining <= 30 ? "text-orange-600" :
                                          "text-yellow-600"
                                        }`}
                                      />
                                      {item.trainingName || item.qualification_type_data?.name || "Qualification expiration"}
                                      {item.daysRemaining < 0 && " (EXPIRED)"}
                                    </>
                                  ) : (
                                    <span>{item.trainingName || item.qualification_type_data?.name || "Qualification expiration"}</span>
                                  )
                                ) : item.type === "training" ? (
                                  <>
                                    <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                                    {item.training?.name || item.description || "Unnamed training"}
                                  </>
                                ) : null}
                              </div>
                              {item.type === "training" && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0" 
                                  onClick={() => handleEditTraining(item)}
                                >
                                  <Edit className="h-3 w-3 text-blue-600" />
                                </Button>
                              )}
                              {isQualificationAlert && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    
                                    // Debug: log the item structure
                                    console.log("Schedule Instance clicked, item structure:", item);
                                    
                                    // Get qualification details from the alert
                                    const qualName = item.trainingName;
                                    const expiryDate = item.expiryDate;
                                    const daysInfo = { 
                                      expired: item.daysRemaining < 0, 
                                      days: Math.abs(item.daysRemaining) 
                                    };
                                    
                                    // Create description with certificate info
                                    let certificateInfo = `Certificate: ${qualName}`;
                                    if (expiryDate) {
                                      certificateInfo += `\nExpiry: ${new Date(expiryDate).toLocaleDateString()}`;
                                      if (daysInfo?.expired) {
                                        certificateInfo += ` (Expired ${daysInfo.days} days ago)`;
                                      } else if (item.daysRemaining <= 30) {
                                        certificateInfo += ` (Expires in ${item.daysRemaining} days)`;
                                      }
                                    }
                                    if (item.certificate_number) {
                                      certificateInfo += `\nCertificate #: ${item.certificate_number}`;
                                    }
                                    
                                    // Open TrainingEditModal with preselected user and qualification info
                                    const trainingDate = new Date(date);
                                    trainingDate.setHours(9, 0, 0, 0);
                                    
                                    // Find the qualification type from userQualifications
                                    const userQual = userQualificationsList.find(q => q.id === item.qualificationId);
                                    console.log("Found userQual:", userQual);
                                    
                                    if (userQual && userQual.qualification_type_data) {
                                      // Create qualification type object similar to CertificateExp
                                      const qualificationType = {
                                        id: userQual.qualification_type_data.id,
                                        name: userQual.qualification_type_data.name
                                      };
                                      
                                      console.log("Setting up TrainingEditData with:", qualificationType);
                                      
                                      setTrainingEditData({
                                        isNew: true,
                                        qualificationType: qualificationType,
                                        users: [user.id],
                                        start_date: trainingDate.toISOString(),
                                        end_date: trainingDate.toISOString(),
                                        description: certificateInfo
                                      });
                                    } else {
                                      console.error("Could not find qualification data for:", item.qualificationId);
                                      // Fallback to basic data
                                      setTrainingEditData({
                                        isNew: true,
                                        users: [user.id],
                                        start_date: trainingDate.toISOString(),
                                        end_date: trainingDate.toISOString(),
                                        description: certificateInfo
                                      });
                                    }
                                    setShowScheduleTraining(true);
                                  }}
                                >
                                  <BookOpen className="h-3 w-3 mr-1" />
                                  Schedule Instance
                                </Button>
                              )}
                            </div>
                            {!isQualificationAlert && (
                              <>
                                {item.type !== "work" && (
                                  <div className="text-xs text-gray-500 mt-1 flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {new Date(item.planned_date_start || item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                                    {" - "}
                                    {new Date(item.planned_date_end || item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                                  </div>
                                )}
                                {item.type === "work" && (
                                  <>
                                    {item.comments && (
                                      <div className="text-xs text-gray-700 mt-1 font-medium">
                                        Scope: {item.comments}
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-600 mt-1 flex items-center">
                                      <MapPin className="h-3 w-3 mr-1" />
                                      {item.location || "No location"}
                                    </div>
                                  </>
                                )}
                                {item.type !== "work" && (
                                  <div className="text-xs text-gray-500 mt-1">{item.location || "No location"}</div>
                                )}
                              </>
                            )}
                            {isQualificationAlert && (
                              <div className="text-xs mt-1 font-medium">
                                {item.certificate_number ? `Certificate #: ${item.certificate_number}` : "No certificate number"}
                              </div>
                            )}
                            {!isQualificationAlert && item.type === "training" && (
                              <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border">
                                <div className="font-medium text-gray-700 mb-1">
                                  Qualification: {item.qualificationName || item.training?.qualification_type?.name || item.training_center_qualification_data?.qualification_type?.name || "Unknown qualification"}
                                </div>
                                {(() => {
                                  const trainingName = item.training?.name || item.description || "";
                                  const commentOrDesc = item.comments || item.description || "";
                                  // Show comments/description only if it's different from training name
                                  if (commentOrDesc && commentOrDesc !== trainingName) {
                                    return (
                                      <div className="text-gray-600">
                                        {commentOrDesc}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            )}
                            {(item.comments || item.description) && !isQualificationAlert && item.type !== "training" && (
                              <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border">
                                {item.comments || item.description}
                              </div>
                            )}
                            {isQualificationAlert && (
                              <div className={`mt-2 text-xs font-medium p-2 rounded border ${
                                item.daysRemaining < 0 ? "bg-red-50 text-red-800 border-red-200" :
                                item.daysRemaining <= 30 ? "bg-orange-50 text-orange-800 border-orange-200" :
                                  item.daysRemaining <= 60 ? "bg-yellow-50 text-yellow-800 border-yellow-200" :
                                    "bg-green-50 border-green-200 text-green-800"
                              }`}>
                                {item.daysRemaining < 0 ? (
                                  <span className="text-red-600">
                                    EXPIRED: {new Date(item.expiryDate).toLocaleDateString()} ({Math.abs(item.daysRemaining)} days ago)
                                  </span>
                                ) : (
                                  <span className={item.daysRemaining <= 30 ? "text-orange-600" : item.daysRemaining <= 60 ? "text-yellow-600" : "text-green-600"}>
                                    Expires: {new Date(item.expiryDate).toLocaleDateString()} ({item.daysRemaining} days left)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : !showScheduleTraining ? (
                  <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
                    <Calendar className="h-10 w-10 text-gray-300 mb-2" />
                    <p className="text-gray-500">No events for this day</p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-gray-700 flex items-center">
                    <BookOpen className="h-4 w-4 mr-2" />
                    User Qualifications ({userQualificationsList.length})
                  </h3>
                  <div className="mt-2 mb-3 flex space-x-2">
                    <Button
                      size=""
                      variant={showExpiringOnly ? "default" : "default"}
                      className={`text-xs py-1 h-7 ${showExpiringOnly ? "bg-blue-200 hover:bg-blue-200" : ""}`}
                      onClick={() => setShowExpiringOnly(!showExpiringOnly)}
                    >
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {showExpiringOnly ? "Show all certificates" : "Show expiring only"}
                    </Button>
                    <Button
                      size=""
                      variant="default"
                      className="text-xs py-1 h-7"
                      onClick={handleAddQualification}
                    >
                      <FilePlus2 className="h-3 w-3 mr-1" />
                      Add Certificate
                    </Button>
                  </div>
                </div>
                
                {getFilteredQualifications().length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    <div className="sticky top-0 z-50 grid grid-cols-12 bg-gray-100 p-2 rounded-t-lg text-xs font-medium">
                      <div className="col-span-3">Qualification</div>
                      <div className="col-span-2">Certificate #</div>
                      <div className="col-span-2">Expiry Date</div>
                      <div className="col-span-3">Status</div>
                      <div className="col-span-2">Action</div>
                    </div>
                    {getFilteredQualifications().map((qual) => {
                      const daysInfo = getDaysRemaining(qual.expiry_date);
                      const statusClass = !qual.expiry_date ? "text-gray-500" :
                                         daysInfo?.expired ? "text-red-600 font-medium" :
                                         daysInfo?.days <= 30 ? "text-orange-600 font-medium" :
                                         daysInfo?.days <= 60 ? "text-yellow-600 font-medium" : "text-green-600";
                      let statusText = "No expiry";
                      if (qual.expiry_date) {
                        if (daysInfo?.expired) statusText = `Expired ${daysInfo.days}d ago`;
                        else if (daysInfo?.days <= 30) statusText = `Warning: ${daysInfo.days}d left`;
                        else if (daysInfo?.days <= 60) statusText = `${daysInfo.days}d left`;
                        else statusText = `${daysInfo.days}d left`;
                      }
                      return (
                        <div 
                          key={qual.id} 
                          className={`grid grid-cols-12 p-2 text-xs bg-green-50 border-b ${daysInfo?.expired ? "bg-red-50" : daysInfo?.days <= 30 && !daysInfo?.expired ? "bg-orange-50" : daysInfo?.days <= 60 && !daysInfo?.expired ? "bg-yellow-50" : ""} transition-colors`}
                        >
                          <div className="col-span-3 font-medium flex items-center cursor-pointer hover:text-blue-600" onClick={() => handleEditQualification(qual)}>
                            <span>{qual.qualification_type_data.name || "Unknown"}</span>
                          </div>
                          <div className="col-span-2">{qual.certificate_number || "—"}</div>
                          <div className={`col-span-2 ${daysInfo?.expired ? "text-red-600" : "text-gray-600"} font-medium`}>
                            {qual.expiry_date ? new Date(qual.expiry_date).toLocaleDateString() : "—"}
                          </div>
                          <div className={`col-span-3 ${statusClass}`}>{statusText}</div>
                          <div className="col-span-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                
                                // Debug: log the qualification structure
                                console.log("Schedule Training clicked from qualifications tab, qual structure:", qual);
                                
                                // Get qualification details
                                const qualName = qual.qualification_type_data.name;
                                const expiryDate = qual.expiry_date;
                                const daysInfo = getDaysRemaining(expiryDate);
                                
                                // Create description with certificate info
                                let certificateInfo = `Certificate: ${qualName}`;
                                if (expiryDate) {
                                  certificateInfo += `\nExpiry: ${new Date(expiryDate).toLocaleDateString()}`;
                                  if (daysInfo?.expired) {
                                    certificateInfo += ` (Expired ${daysInfo.days} days ago)`;
                                  } else if (daysInfo?.days <= 30) {
                                    certificateInfo += ` (Expires in ${daysInfo.days} days)`;
                                  }
                                }
                                if (qual.certificate_number) {
                                  certificateInfo += `\nCertificate #: ${qual.certificate_number}`;
                                }
                                
                                // Open TrainingEditModal with preselected user and qualification info
                                const trainingDate = new Date(date);
                                trainingDate.setHours(9, 0, 0, 0);
                                
                                // Create qualification type object similar to CertificateExp
                                const qualificationType = {
                                  id: qual.qualification_type_data.id,
                                  name: qual.qualification_type_data.name
                                };
                                
                                console.log("Setting up TrainingEditData from qualifications tab with:", qualificationType);
                                
                                setTrainingEditData({
                                  isNew: true,
                                  qualificationType: qualificationType,
                                  users: [user.id],
                                  start_date: trainingDate.toISOString(),
                                  end_date: trainingDate.toISOString(),
                                  description: certificateInfo
                                });
                                setShowScheduleTraining(true);
                              }}
                            >
                              
                              Schedule Training
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
                    <BookOpen className="h-10 w-10 text-gray-300 mb-2" />
                    <p className="text-gray-500">No qualifications found for this user</p>
                  </div>
                )}
                {userQualificationsList.length > 0 && (
                  <div className="mt-4 bg-gray-50 p-3 rounded-lg border text-xs">
                    <h4 className="font-medium mb-4 text-center">Qualification Status Summary:</h4>
                    <div className="grid grid-cols-4 gap-2">                      
                      <div className="flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                        <span>Expired: {userQualificationsList.filter(q => q.expiry_date && getDaysRemaining(q.expiry_date).expired).length}</span>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                        <span>0-30d: {userQualificationsList.filter(q => q.expiry_date && !getDaysRemaining(q.expiry_date).expired && getDaysRemaining(q.expiry_date).days <= 30).length}</span>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                        <span>31-60d: {userQualificationsList.filter(q => q.expiry_date && !getDaysRemaining(q.expiry_date).expired && getDaysRemaining(q.expiry_date).days > 30 && getDaysRemaining(q.expiry_date).days <= 60).length}</span>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                        <span>Valid: {userQualificationsList.filter(q => !q.expiry_date || (!getDaysRemaining(q.expiry_date).expired && getDaysRemaining(q.expiry_date).days > 60)).length}</span>
                      </div>
                    </div>
                  </div>
                )}
                
              </div>
            )}
            </div>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        
        
        {editingQualification !== undefined && (
          <EditQualificationDialog
            isOpen={editingQualification !== undefined}
            onClose={() => setEditingQualification(undefined)}
            qualificationData={editingQualification}
            fetchData={fetchData}
            userId={user.id}
          />
        )}
        
        {/* New Training Modal */}
        {showScheduleTraining && trainingEditData && (
          <TrainingEditModal
            isOpen={showScheduleTraining}
            onClose={() => {
              setShowScheduleTraining(false);
              setTrainingEditData(null);
            }}
            trainingData={trainingEditData}
            fetchData={fetchData}
          />
        )}
        
      </DialogContent>
    </Dialog>
  );
};

export default UserDayView;